import { ComponentRef, Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  createUrlTreeFromSnapshot,
  DetachedRouteHandle,
  OutletContext,
  UrlSerializer,
} from '@angular/router';

/**
 * See https://www.intertech.com/angular-development-10-routereusestrategy-maintaining-component-state/
 * for background to this class.
 * TODO This is just a v0.1. Most certainly we will need more control on when we reuse routes and when not.
 */
@Injectable()
export class AlooraReuseStrategy extends BaseRouteReuseStrategy {
  private urlSerializer = inject(UrlSerializer);

  private savedHandles = new Map<string, DetachedRouteHandle>();

  /**
   * Whether the given route should detach for later reuse.
   * For the moment we only store the routes for Your Labs / the mobile app
   * TODO this needs to change, and we also need to determine when to remove stale routes from the store.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    return key.includes('orders');
  }

  deleteRouteStore(): void {
    this.savedHandles.forEach((handle) => this.deactivateOutlet(handle));
    this.savedHandles.clear();
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.getRouteKey(route);
    if (handle == null) {
      this.savedHandles.delete(key);
    } else {
      this.savedHandles.set(key, handle);
    }
  }

  /**
   * We reattach whenever we have a stored handle for the requested route.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    // see https://github.com/angular/angular/issues/44383 for some background
    if (!route.component) {
      return false;
    }
    const key = this.getRouteKey(route);
    return this.savedHandles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    return this.savedHandles.get(key) ?? null;
  }

  /**
   * We use the url as key, which we can get from the snapshot.
   */
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const urlTree = createUrlTreeFromSnapshot(route, ['.']);
    const url = this.urlSerializer.serialize(urlTree);
    return url;
  }

  private deactivateOutlet(handle: DetachedRouteHandle): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const contexts: Map<string, OutletContext> = (handle as any)['contexts'];
    contexts.forEach((context: OutletContext, key: string) => {
      if (context.outlet) {
        // Destroy the component
        context.outlet.deactivate();
        // Destroy the contexts for all the outlets that were in the component
        context.children.onOutletDeactivated();
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const componentRef: ComponentRef<unknown> = (handle as any)['componentRef'];
    componentRef.destroy();
  }
}
